import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface CashuContextValue {
  balance: number;
  isLoading: boolean;
  refreshBalance: () => Promise<void>;
  requestMint: (amount: number) => Promise<{ pr: string; hash: string }>;
  checkMintQuote: (hash: string) => Promise<boolean>;
  mintTokens: (hash: string) => Promise<void>;
  sendTokens: (amount: number) => Promise<string>;
  receiveTokens: (token: string) => Promise<number>;
}

const CashuContext = createContext<CashuContextValue | undefined>(undefined);

// Simple mock implementation for now - will be replaced with actual Cashu integration
export function CashuProvider({ children }: { children: ReactNode }) {
  const [balance, setBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load balance from localStorage
  useEffect(() => {
    const storedBalance = localStorage.getItem('cashu_balance');
    if (storedBalance) {
      setBalance(parseInt(storedBalance));
    }
  }, []);

  // Save balance to localStorage
  const saveBalance = useCallback((newBalance: number) => {
    localStorage.setItem('cashu_balance', newBalance.toString());
    setBalance(newBalance);
  }, []);

  const refreshBalance = useCallback(async () => {
    // In a real implementation, this would check with the mint
    const storedBalance = localStorage.getItem('cashu_balance');
    if (storedBalance) {
      setBalance(parseInt(storedBalance));
    }
  }, []);

  const requestMint = useCallback(async (amount: number): Promise<{ pr: string; hash: string }> => {
    // Mock implementation - generate a fake invoice
    const hash = Math.random().toString(36).substring(7);
    const pr = `lnbc${amount}1fake_invoice_${hash}`;
    
    return { pr, hash };
  }, []);

  const checkMintQuote = useCallback(async (hash: string): Promise<boolean> => {
    // Mock implementation - return true after a delay to simulate payment
    return new Promise((resolve) => {
      setTimeout(() => resolve(true), 5000);
    });
  }, []);

  const mintTokens = useCallback(async (hash: string) => {
    // Mock implementation - just add the balance
    const mintAmount = parseInt(hash) || 1000; // Default to 1000 sats
    saveBalance(balance + mintAmount);
  }, [balance, saveBalance]);

  const sendTokens = useCallback(async (amount: number): Promise<string> => {
    if (balance < amount) {
      throw new Error('Insufficient balance');
    }
    
    // Generate a mock Cashu token
    const token = `cashuA${btoa(JSON.stringify({ amount, timestamp: Date.now() }))}`;
    
    // Deduct from balance
    saveBalance(balance - amount);
    
    return token;
  }, [balance, saveBalance]);

  const receiveTokens = useCallback(async (token: string): Promise<number> => {
    try {
      // Decode the mock token
      const decoded = JSON.parse(atob(token.replace('cashuA', '')));
      const amount = decoded.amount;
      
      // Add to balance
      saveBalance(balance + amount);
      
      return amount;
    } catch (error) {
      throw new Error('Invalid token');
    }
  }, [balance, saveBalance]);

  const value: CashuContextValue = {
    balance,
    isLoading,
    refreshBalance,
    requestMint,
    checkMintQuote,
    mintTokens,
    sendTokens,
    receiveTokens,
  };

  return <CashuContext.Provider value={value}>{children}</CashuContext.Provider>;
}

export function useCashu() {
  const context = useContext(CashuContext);
  if (context === undefined) {
    throw new Error('useCashu must be used within a CashuProvider');
  }
  return context;
}
